// src/components/checklists/ChecklistSendDialog.js
// custcopy-32471cbd — CHECKLISTS SEAM 8c: "Send to customer" (web job pop-up, office roles).
//
// To: pre-filled with the customer's email, editable, ONE address. Subject: "Your <checklist> — Job #<n>".
// Message: optional, up to 2,000 characters. The PDF chip shows what is attached; Preview downloads the
// same PDF Print / PDF makes. An unfinished checklist asks "send anyway?" first, then sends with
// sendUnfinished: true. Every rule is re-checked by the server (sendChecklistCopy) — office roles, customer
// copy on, the limits — and its refusal sentence is shown here as it comes back.
// The email goes From the company name; replies go to the company email (never Spatchy).
// clfix-56ea256b M2 — Office Staff pick the To address from the emails on file (the customer's, or a
// property's contact email); Owner / Admin / Manager may type any address (the on-file ones are offered).
// sendChecklistCopy enforces the same rule.

import React, { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { Send, X, Loader2, FileText, Eye } from 'lucide-react';
import { db } from '../../firebase/config';
import {
    sendChecklistCopy, getChecklistCopyPdf, downloadPdfBytes,
    defaultChecklistSubject, isOneEmailAddress, SEND_SUBJECT_MAX, SEND_MESSAGE_MAX, CHECKLIST_UNFINISHED_ASK,
    canSendToAnyAddress, checklistEmailsOnFile, NO_EMAIL_ON_FILE, // clfix-56ea256b
} from '../../utils/checklistClient';

const UNFINISHED_ASK = CHECKLIST_UNFINISHED_ASK; // the same question as the invoice banner (8d)

const ChecklistSendDialog = ({ isOpen, onClose, orgId, job, checklist, onSent, currentUserProfile }) => {
    const [to, setTo] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [busy, setBusy] = useState(''); // '' | 'send' | 'preview'
    const [error, setError] = useState('');
    const [loadingEmail, setLoadingEmail] = useState(false);
    const [onFile, setOnFile] = useState([]); // clfix-56ea256b — the customer's emails on file
    const anyAddress = canSendToAnyAddress(currentUserProfile);

    useEffect(() => {
        if (!isOpen || !checklist) return undefined;
        let live = true;
        setSubject(defaultChecklistSubject(checklist.name, job?.jobNumber));
        setMessage('');
        setError('');
        setBusy('');
        setTo('');
        setOnFile([]);
        if (orgId && job?.clientId) {
            setLoadingEmail(true);
            getDoc(doc(db, `organizations/${orgId}/customers`, job.clientId))
                .then((snap) => {
                    if (!live) return;
                    const list = checklistEmailsOnFile(snap.exists() ? [snap.data()] : []);
                    setOnFile(list);
                    setTo(list[0] || '');
                })
                .catch(() => { /* leave To empty; the person types it */ })
                .finally(() => { if (live) setLoadingEmail(false); });
        }
        return () => { live = false; };
    }, [isOpen, checklist, job, orgId]);

    if (!isOpen || !checklist) return null;

    const unfinished = checklist.status !== 'complete';
    const toOk = isOneEmailAddress(to);
    const subjectOk = subject.trim().length > 0 && subject.trim().length <= SEND_SUBJECT_MAX;
    const canSend = toOk && subjectOk && message.length <= SEND_MESSAGE_MAX && !busy;

    const handlePreview = async () => {
        setBusy('preview');
        setError('');
        try {
            const res = await getChecklistCopyPdf({ jobId: job.id, checklistId: checklist.id });
            downloadPdfBytes(res.pdfBase64, res.fileName);
        } catch (err) {
            setError(err?.message || 'Could not make the PDF. Please try again.');
        } finally {
            setBusy('');
        }
    };

    const handleSend = async () => {
        if (!canSend) return;
        if (unfinished && !window.confirm(UNFINISHED_ASK)) return;
        setBusy('send');
        setError('');
        try {
            await sendChecklistCopy({
                jobId: job.id, checklistId: checklist.id, to: to.trim(), subject: subject.trim(), message,
                ...(unfinished ? { sendUnfinished: true } : {}),
            });
            if (onSent) onSent(to.trim());
            onClose();
        } catch (err) {
            setError(err?.message || 'The email could not be sent. Please try again.');
        } finally {
            setBusy('');
        }
    };

    const INPUT = 'w-full text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#5F4EC9]/30';

    return (
        <div className="fixed inset-0 bg-black/50 z-[70] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg" role="dialog" aria-modal="true" aria-label={`Send ${checklist.name} to the customer`}>
                <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-100">
                    <div className="flex items-start gap-2 min-w-0">
                        <Send size={18} className="text-[#5F4EC9] mt-0.5 flex-shrink-0" />
                        <h2 className="text-base font-bold text-[#12192D] truncate">Send to customer</h2>
                    </div>
                    <button type="button" onClick={onClose} disabled={busy === 'send'} aria-label="Close"
                        className="p-1 rounded-lg hover:bg-slate-100 text-slate-400">
                        <X size={16} />
                    </button>
                </div>

                <div className="p-5 space-y-3">
                    {unfinished && (
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                            This checklist isn't finished ({Number(checklist.requiredAnswered) || 0} of {Number(checklist.requiredTotal) || 0} required answered). You'll be asked to confirm before it's sent.
                        </p>
                    )}
                    <div>
                        <label htmlFor="cl-send-to" className="block text-xs font-semibold text-slate-600 mb-1">To</label>
                        {anyAddress ? (
                            <>
                                <input id="cl-send-to" type="email" value={to} disabled={busy === 'send'} list="cl-send-on-file"
                                    placeholder={loadingEmail ? 'Loading the customer’s email…' : 'customer@example.com'}
                                    onChange={(e) => { setTo(e.target.value); setError(''); }}
                                    className={`${INPUT} ${to && !toOk ? 'border-red-300' : ''}`} />
                                <datalist id="cl-send-on-file">{onFile.map((e) => <option key={e} value={e} />)}</datalist>
                                {to && !toOk && <p className="mt-1 text-[11px] text-red-600">Enter one email address.</p>}
                            </>
                        ) : loadingEmail ? (
                            <p className="text-sm text-slate-400">Loading the customer’s email…</p>
                        ) : onFile.length ? (
                            <>
                                <select id="cl-send-to" value={to} disabled={busy === 'send'}
                                    onChange={(e) => { setTo(e.target.value); setError(''); }} className={INPUT}>
                                    {onFile.map((e) => <option key={e} value={e}>{e}</option>)}
                                </select>
                                <p className="mt-1 text-[11px] text-slate-500">Emails on file for this customer and their properties. An Owner, Admin or Manager can send to another address.</p>
                            </>
                        ) : (
                            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">{NO_EMAIL_ON_FILE}</p>
                        )}
                    </div>
                    <div>
                        <label htmlFor="cl-send-subject" className="block text-xs font-semibold text-slate-600 mb-1">Subject</label>
                        <input id="cl-send-subject" type="text" value={subject} maxLength={SEND_SUBJECT_MAX} disabled={busy === 'send'}
                            onChange={(e) => { setSubject(e.target.value); setError(''); }} className={INPUT} />
                    </div>
                    <div>
                        <label htmlFor="cl-send-message" className="block text-xs font-semibold text-slate-600 mb-1">Message</label>
                        <textarea id="cl-send-message" rows={5} value={message} maxLength={SEND_MESSAGE_MAX} disabled={busy === 'send'}
                            placeholder={`Please find your ${checklist.name} attached.`}
                            onChange={(e) => { setMessage(e.target.value); setError(''); }}
                            className={`${INPUT} resize-none`} />
                        <p className="text-[10px] text-slate-400 text-right">{message.length.toLocaleString()} / {SEND_MESSAGE_MAX.toLocaleString()}</p>
                    </div>
                    <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2">
                        <span className="flex items-center gap-2 min-w-0 text-sm text-slate-700">
                            <FileText size={16} className="text-rose-500 flex-shrink-0" />
                            <span className="truncate">{checklist.name} (PDF)</span>
                        </span>
                        <button type="button" onClick={handlePreview} disabled={!!busy}
                            className="flex items-center gap-1 px-2 py-1 text-xs font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg disabled:opacity-50">
                            {busy === 'preview' ? <Loader2 size={12} className="animate-spin" /> : <Eye size={12} />} Preview
                        </button>
                    </div>
                    <p className="text-[11px] text-slate-500">Sent from your company name. When the customer replies, it goes to your company email.</p>
                    {error && <p className="text-xs text-red-600">{error}</p>}
                </div>

                <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-100">
                    <button type="button" onClick={onClose} disabled={busy === 'send'}
                        className="px-3 py-1.5 text-sm font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50">
                        Cancel
                    </button>
                    <button type="button" onClick={handleSend} disabled={!canSend}
                        className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-semibold text-white bg-[#5F4EC9] hover:bg-[#4f3fb5] rounded-lg disabled:opacity-50">
                        {busy === 'send' ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChecklistSendDialog;
